import React, {useCallback, useEffect, useState} from 'react';
import { browserHistory } from 'react-router';
import PropTypes from "prop-types";
import Select from 'react-select';
import LoadingIndicatorHeader from '../loading-indicator/loading-indicator-header';
import CampaignFormButtons from './campaign-form-buttons';
import campaignFormValidation from './campaign-form-validation';
import ajax from '../util/util-ajax';
import util from '../util/util';
import Tooltip from "../tooltip/tooltip";
import _ from "lodash";
import { v4 } from 'uuid';

const jQuery = window.jQuery;

export default function CampaignForm (props) {
  // id of the campaign we are editing (0 means new campaign to create)
  const [id, setId] = useState(0);
  // indicator for when the form has loaded
  const [loaded, setLoaded] = useState(false);
  // name of the campaign we are editing
  const [name, setName] = useState(``);
  // prop names determine which errors to show
  const [errors, setErrors] = useState({});
  // array used to control the SELECT fields for pods (does not contain the values to pass to Django)
  const [pods, setPods] = useState([]);
  // used to control the message displayed on the SELECT while searching
  const [searchingForPods, setSearchingForPods] = useState(false);
  // the Options for the SELECT
  const [podOptions, setPodOptions] = useState([]);
  // used to delete the old pods from campaign, on editing an existing campaign
  const [originalPods, setOriginalPods] = useState([]);

  useEffect( () => {
    // id of the campaign we are editing (0 means new campaign to create)
    const id = props.params ? Number(props.params.id) : 0;

    // get the campaign data to display in the form;
    const getCampaign = () => {
      // on a new campaign, no need to set up all the values as the default ones are used
      if (id === 0) {
        // if a pod was defined, then set it as the value of the SELECT;
        if (props.params && props.params.podId) {
          util.getPod(props.params.podId)
            .then((data) => {
              setPods([{
                label: data.name,
                value: data.id,
                id: data.id.toString()
              }]);
              setLoaded(true);
            });
        // otherwise use the empty value and tell the component that we're done loading;
        } else {
          setPods(getDefaultPodValue());
          setLoaded(true);
        }

      // on an existing campaign
      } else {
        // fire off both Ajax requests needed for this form;
        // once both have resolved, save the data and set loaded to true;
        jQuery
          .when(
            util.getCampaign(id),
            util.getPodsForCampaign(id)
          )
          .then((campaignData, podsData) => {
            setTimeout(() => {
              setId(id);
              setName(campaignData[0].name);
              setOriginalPods(util.returnSimpleSelectOptions(podsData[0].results));
              setPods(podsData[0].results.length ? util.returnSimpleSelectOptions(podsData[0].results) : getDefaultPodValue())
              setLoaded(true);
            }, util.delayDetail);
          });
      }
    }

    getCampaign();
    setLoaded(true);
  }, [props.params]);

  // default value of the SELECT;
  const getDefaultPodValue = () => {
    return [{
      label: `Select a pod`,
      value: 0,
      id: v4()
    }]
  };

  // add another form field to the page so the user can select another pod;
  const addPod = () => {
    let podsArray;
    podsArray = Array.from(pods);
    podsArray.push(getDefaultPodValue()[0]);
    setPods(podsArray);
  }

  const getPodSearchStatus = (searchTerm) => {
    if (searchTerm.inputValue.length === 0) {
      return `Please type a few characters to kick off remote search.`
    }
    if (searchingForPods === true) {
      return `Searching...`
    }
    if (podOptions.length === 0) {
      return `No results found.`
    }
    return `Something went wrong.`
  }

  const getPodSearchResults = (searchTerm) => {
    if (searchTerm.length === 0) {
      setSearchingForPods(false);
      setPodOptions([]);
    } else {
      setSearchingForPods(true);
      util
        .getPods({
          sort: `name`,
          page: 1,
          search: searchTerm,
          aws_status: 2,
        })
        .then((data) => {
          setSearchingForPods(false);
          setPodOptions(util.returnSimpleSelectOptions(data.results));
        });
    }
  }

  // used to delay the pod search start
  const getPodSearchResultsDelayed = useCallback(
    _.debounce(getPodSearchResults, 250),
    []
  )

  const handlePodsOnValueChange = (selectedPod, action, podIndex) => {
    let podsArray;
    podsArray = Array.from(pods);
    if (action.action === "select-option") {
      podsArray[podIndex] = {
        "label": selectedPod.label,
        "value": selectedPod.value,
        "id": selectedPod.value.toString()
      };
    }
    else if (action.action === "clear") {
      if (podsArray.length === 1) {
        podsArray[0] = getDefaultPodValue()[0];
      } else {
        podsArray.splice(podIndex, 1);
      }
    }
    setPods(podsArray);
  }

  // submit the form;
  const onSubmit = () => {
    // build the object to send to the server;
    const report = {
      id: id,
      name: name.trim(),
      pods: []
    };

    // see if there are any errors before proceeding;
    const validationResult = campaignFormValidation(report);

    // danger will robinson, we have errors;
    if (validationResult.hasErrors) {
      setErrors(validationResult.errors);
      util.putFoucsOnError();

      // stop any further processing;
      return false;

    // good to go, clear any existing errors;
    } else {
      setErrors({});
    }

    // insert only valid pods into the obj to send;
    pods && pods.forEach((pod) => {
      const newPodId = pod.value ? Number(pod.value) : null;

      if (newPodId) {
        // don't allow doubles;
        if (report.pods.indexOf(newPodId) === -1) {
          report.pods.push(newPodId);
        }
      }
    });

    // storage for the campaign data on save;
    let campaignData = null;

    // put the button into a processing state;
    const $button = jQuery(`.btn-primary`).button(`loading`);

    // save the form data;
    util.saveCampaign(id, report)
      .fail((error) => {
        const serializerNameExistsError = "A Campaign with this name already exists.";
        const api_error = _.get(error, "responseJSON.Validation_Error[0]")

        // if the name already exists
        if (serializerNameExistsError === api_error) {
          setErrors(_.merge({}, errors, { name: api_error}));
          util.putFoucsOnError();
        // if there is a different backend (4xx, 5xx) error
        } else {
          ajax.onFail(error);
        }

        setTimeout(() => {
          // reset the button on fail so it can be clicked again;
          $button.button(`reset`);
        }, util.delayForm);

        // stop any further processing, as the Campaign was not saved in the DB
        return false;
      })
      .then((data) => {
        // needed for subsequent ajax calls;
        campaignData = data;

        const removeRequests = originalPods.map((pod) => {
          return util.deletePodFromCampaign(data.id, pod.value);
        });

        // start the removals;
        return jQuery.when.apply(jQuery, removeRequests);
      })
      .fail(() => {
        setTimeout(() => {
          $button.button(`reset`);
        }, util.delayForm);
      })
      .then(() => {
        const addRequests = report.pods.map((pod) => {
          return util.savePodToCampaign(campaignData.id, pod);
        });

        // start the additions;
        return jQuery.when.apply(jQuery, addRequests);
      })
      .fail(() => {
        setTimeout(() => {
          // reset the button on fail so it can be clicked again;
          $button.button(`reset`);
        }, util.delayForm);
      })
      .then(() => {
        // wait a little bit (so the user can track what’s going on);
        setTimeout(() => {
          const url = util.returnCampaignDetailUrl(campaignData.id);

          // load the next page;
          browserHistory.push(url);
        }, util.delayForm);
      });
  }

  // cancel this action (do nothing and go somewhere else);
  const onCancel = () => {
    const newUrl = (id === 0) ? util.returnCampaignListUrl() : util.returnCampaignDetailUrl(id);

    browserHistory.push(newUrl);
  }

  if (loaded === false) {
    return (
      <LoadingIndicatorHeader />
    );
  } else {
    const tooltipMessage = "Only pods in a 'Ready' status<br />can be scheduled.";
    return (
      <form className="campaign-form" action="#">
        <div className="row">
          <div className="col-sm-6">
            <h4 className="page__title">{ id === 0 ? `New Campaign` : `Edit Campaign` }</h4>
          </div>
          <div className="col-sm-6 hidden-xs">
            <CampaignFormButtons
              onCancel={ () => onCancel() }
              onSubmit={ () => onSubmit() } />
          </div>
        </div>

        <hr />

        <div className="row">
          <div className="col-sm-6">
            <div className={ errors.name ? `form-group has-error` : `form-group` }>
              <label className="control-label" htmlFor="name">Campaign Name</label>
              <input
                type="text"
                id="name"
                className="form-control"
                value={ name }
                autoFocus
                onChange={ (e) => setName(e.target.value) }
                maxLength="100" />
              <p className={ errors.name ? `help-block` : `hidden` }>
                { errors.name }
              </p>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label className="control-label">
                Add Pod{ pods.length > 1 ? `(s)` :  `` }&nbsp;
                <i
                  data-tip={tooltipMessage}
                  data-for="campaignFormPodStatus"
                  className="glyphicon glyphicon-question-sign"
                  aria-hidden="true"
                ></i>
              </label>
              <Tooltip id="campaignFormPodStatus" offset_top={"-6"}/>
              <div className="well">
                {
                  pods.map((pod, index) => (
                    <div className="form-group" key={index}>
                      <Select
                        name={pod.id}
                        value={pod}
                        options={podOptions}
                        placeholder="Select a pod"
                        theme="bootstrap3"
                        isClearable={true}
                        blurInputOnSelect
                        filterOption={
                          (dbOption, userInput) => {return dbOption;}
                        }
                        noOptionsMessage={
                          (searchTerm) => {
                            return (
                              <div className="no-results-found">
                                {getPodSearchStatus(searchTerm)}
                              </div>
                            );
                          }
                        }
                        onInputChange={
                          (searchTerm) => {
                            // updating the search status on the UI
                            setSearchingForPods(true);
                            // making the backend call, with delay
                            getPodSearchResultsDelayed(searchTerm);
                          }
                        }
                        onChange={
                          (value, action) => {
                            handlePodsOnValueChange(value, action, index);
                          }
                        }
                      />
                    </div>
                  ))
                }
              </div>
              <button
                type="button"
                id="campaign-form__add--pod"
                className="btn btn-default"
                onClick={ () => addPod() }>
                <i className="fa fa-plus" aria-hidden="true"></i>
                Add Another Pod
              </button>
            </div>
          </div>
        </div>

        <hr />

        <CampaignFormButtons
          onCancel={ () => onCancel() }
          onSubmit={ () => onSubmit() } />
      </form>
    );
  }
}

CampaignForm.propTypes = {
  params: PropTypes.object
};
